import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Shield, Search, Star, ArrowRight, Wrench, User } from 'lucide-react';
import { api } from '../../utils/api';
import { useTalentAuth } from '../../contexts/TalentAuthContext';
import '../../globals.css';

interface Suggestion {
  type: string;
  title: string;
  description: string;
  count: number;
  icon: string;
}

interface LocationSuggestion {
  type: string;
  name: string;
  full_name: string;
  worker_count: number;
  icon: string;
}

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const { talent, isAuthenticated } = useTalentAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  // Location suggestions state
  const [locationSuggestions, setLocationSuggestions] = useState<LocationSuggestion[]>([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(-1);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const searchRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const locationDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Sync state with URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q') || '';

    // Check specific location params first, then generic 'location'
    const loc = params.get('parish') || params.get('city') || params.get('location') || '';

    setSearchQuery(q);
    setLocationQuery(loc);

    // Also fetch suggestions if we have pre-populated values
    if (q.length >= 2) {
      fetchSuggestions(q);
    }
    if (loc.length >= 2) {
      fetchLocationSuggestions(loc);
    }
  }, [location.search]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
      if (locationRef.current && !locationRef.current.contains(event.target as Node)) {
        setShowLocationSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      setIsLoading(true);
      const data = await api.get(`/api/search/suggestions?q=${encodeURIComponent(query)}`);

      if (data.success) {
        setSuggestions(data.suggestions);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLocationSuggestions = async (query: string) => {
    if (query.length < 2) {
      setLocationSuggestions([]);
      return;
    }

    try {
      setIsLocationLoading(true);
      const data = await api.get(`/api/search/locations?q=${encodeURIComponent(query)}`);

      if (data.success) {
        setLocationSuggestions(data.suggestions);
      }
    } catch (error) {
      console.error('Error fetching location suggestions:', error);
    } finally {
      setIsLocationLoading(false);
    }
  };

  const handleSearchInputChange = (value: string) => {
    setSearchQuery(value);
    setSelectedSuggestionIndex(-1);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);

    setShowSuggestions(true);
  };

  const handleLocationInputChange = (value: string) => {
    setLocationQuery(value);
    setSelectedLocationIndex(-1);

    if (locationDebounceRef.current) {
      clearTimeout(locationDebounceRef.current);
    }

    locationDebounceRef.current = setTimeout(() => {
      fetchLocationSuggestions(value);
    }, 300);

    setShowLocationSuggestions(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedSuggestionIndex]);
        } else {
          handleSearch();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  const handleLocationKeyDown = (e: React.KeyboardEvent) => {
    if (!showLocationSuggestions || locationSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedLocationIndex(prev =>
          prev < locationSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedLocationIndex(prev =>
          prev > 0 ? prev - 1 : locationSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedLocationIndex >= 0 && selectedLocationIndex < locationSuggestions.length) {
          handleLocationSuggestionClick(locationSuggestions[selectedLocationIndex]);
        } else {
          handleSearch();
        }
        break;
      case 'Escape':
        setShowLocationSuggestions(false);
        setSelectedLocationIndex(-1);
        break;
    }
  };

  const handleSearch = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    const params = new URLSearchParams(location.search);

    // Set query
    if (searchQuery.trim()) {
      params.set('q', searchQuery.trim());
    } else {
      params.delete('q');
    }

    // If the user manually typed a location, we use the generic 'location' param
    // and clear any specific 'parish' or 'city' params that might have been there from a previous suggestion click
    if (locationQuery.trim()) {
      const currentParish = params.get('parish');
      const currentCity = params.get('city');

      // If the location matches exactly a previously set parish/city, keep it
      // Otherwise, treat it as a generic location search
      if (locationQuery !== currentParish && locationQuery !== currentCity) {
        params.delete('parish');
        params.delete('city');
        params.set('location', locationQuery.trim());
      }
    } else {
      params.delete('location');
      params.delete('parish');
      params.delete('city');
    }

    // Reset pagination on new search
    params.delete('page');

    navigate(`/search?${params.toString()}`);
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    setSearchQuery(suggestion.title);
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);

    const params = new URLSearchParams(location.search);
    params.set('q', suggestion.title);
    params.delete('page');
    navigate(`/search?${params.toString()}`);
  };

  const handleLocationSuggestionClick = (suggestion: LocationSuggestion) => {
    setLocationQuery(suggestion.full_name);
    setShowLocationSuggestions(false);
    setSelectedLocationIndex(-1);

    // Immediately navigate or store the type for handleSearch
    const params = new URLSearchParams(location.search);
    if (searchQuery.trim()) {
      params.set('q', searchQuery.trim());
    }

    // Clear old location params
    params.delete('location');
    params.delete('parish');
    params.delete('city');

    // Set specific location param based on type
    if (suggestion.type === 'parish') {
      params.set('parish', suggestion.full_name);
    } else if (suggestion.type === 'city') {
      params.set('city', suggestion.full_name);
    } else {
      params.set('location', suggestion.full_name);
    }

    navigate(`/search?${params.toString()}`);
  };
  return (
    <header className="premium-header">
      <div className="premium-header-container">
        {/* Logo */}
        <Link className="d-flex align-items-center gap-2" to="/" style={{ textDecoration: 'none' }}>
          <div className="d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', borderRadius: '8px', backgroundColor: '#185FA5' }}>
            <svg viewBox="0 0 24 24" fill="#FAC775" style={{ width: '20px', height: '20px' }}>
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
          </div>
          <span style={{ fontSize: '22px', fontWeight: 'bold', color: '#185FA5', marginLeft: '8px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>SkillSeek</span>
        </Link>

        {/* Navigation links (Desktop) */}
        <nav className="premium-nav-links d-none d-md-flex">
          <Link className={`premium-nav-link ${location.pathname === '/' ? 'active' : ''}`} to="/">Home</Link>
          <Link className={`premium-nav-link ${location.pathname === '/about' ? 'active' : ''}`} to="/about">About Us</Link>
          <Link className={`premium-nav-link ${location.pathname === '/listing' || location.pathname === '/search' ? 'active' : ''}`} to="/listing">Services</Link>
          <Link className={`premium-nav-link ${location.pathname === '/contact' ? 'active' : ''}`} to="/contact">Contact Us</Link>
        </nav>

        {/* Actions (Desktop & Mobile trigger) */}
        <div className="d-flex align-items-center gap-3">
          <div className="d-none d-sm-inline-flex">
            {isAuthenticated && talent ? (
              <Link to="/talent/dashboard" className="premium-btn-outline">
                <User size={16} />
                <span>Dashboard</span>
              </Link>
            ) : (
              <Link to="/login" className="premium-btn-outline">
                <User size={16} />
                <span>Sign In</span>
              </Link>
            )}
          </div>

          {/* Mobile menu toggle */}
          <button 
            className="d-md-none btn p-2 rounded-lg" 
            style={{ backgroundColor: '#185FA5', color: '#ffffff', border: 'none' }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {mobileMenuOpen ? (
                <line x1="18" y1="6" x2="6" y2="18"></line>
              ) : (
                <>
                  <line x1="4" y1="12" x2="20" y2="12"></line>
                  <line x1="4" y1="6" x2="20" y2="6"></line>
                  <line x1="4" y1="18" x2="20" y2="18"></line>
                </>
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Nav Links dropdown */}
      {mobileMenuOpen && (
        <div className="d-md-none position-absolute w-100 bg-white border-bottom" style={{ top: '57px', left: 0, zIndex: 999, padding: '16px 24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
          <nav className="d-flex flex-column gap-3">
            <Link className={`premium-nav-link ${location.pathname === '/' ? 'active' : ''}`} to="/" onClick={() => setMobileMenuOpen(false)}>Home</Link>
            <Link className={`premium-nav-link ${location.pathname === '/about' ? 'active' : ''}`} to="/about" onClick={() => setMobileMenuOpen(false)}>About Us</Link>
            <Link className={`premium-nav-link ${location.pathname === '/listing' || location.pathname === '/search' ? 'active' : ''}`} to="/listing" onClick={() => setMobileMenuOpen(false)}>Services</Link>
            <Link className={`premium-nav-link ${location.pathname === '/contact' ? 'active' : ''}`} to="/contact" onClick={() => setMobileMenuOpen(false)}>Contact Us</Link>
            <hr style={{ margin: '8px 0', borderColor: '#F3F4F6' }} />
            {isAuthenticated && talent ? (
              <Link to="/talent/dashboard" className="premium-btn-outline justify-content-center" onClick={() => setMobileMenuOpen(false)}>
                <User size={16} />
                <span>Dashboard</span>
              </Link>
            ) : (
              <Link to="/login" className="premium-btn-outline justify-content-center" onClick={() => setMobileMenuOpen(false)}>
                <User size={16} />
                <span>Sign In</span>
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

export default Header;
